<template>
  <div class="n-avatar">
    <n-tooltip position="top" :text="name" v-if="enableTooltip">
      <div
        class="n-avatar__circle"
        @click="$emit('click')"
        :class="{
          'n-avatar__circle--small': size === 's',
          'n-avatar__circle--large': size === 'l',
        }"
        :style="{
          'background-color': imageUrl && !imageLoadError ? 'transparent' : stringToHSLColour(name),
        }"
      >
        <img
          :src="imageUrl"
          :alt="`Avatar image for ${name}`"
          v-if="imageUrl && !imageLoadError"
          v-show="imageLoaded"
          @load="handleImageLoad"
          @error="handleImageErr"
        />
        <span v-else>{{ initials }}</span>
      </div>
    </n-tooltip>
    <div v-else>
      <div
        class="n-avatar__circle"
        @click="$emit('click')"
        :class="{
          'n-avatar__circle--small': size === 's',
          'n-avatar__circle--large': size === 'l',
        }"
        :style="{
          'background-color': imageUrl && !imageLoadError ? 'transparent' : stringToHSLColour(name),
        }"
      >
        <img
          :src="imageUrl"
          :alt="`Avatar image for ${name}`"
          v-if="imageUrl && !imageLoadError"
          v-show="imageLoaded"
          @load="handleImageLoad"
          @error="handleImageErr"
        />
        <span v-else>{{ initials }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref, watch, computed } from 'vue';
import NTooltip from '../../control/tooltip/tooltip.vue';

defineOptions({
  name: 'n-avatar',
});

const props = defineProps({
  imageUrl: {
    type: String,
  },
  size: {
    type: String as PropType<'s' | 'm' | 'l'>,
    default: 'm',
  },
  name: {
    type: String,
    default: 'unknown',
  },
  enableTooltip: {
    type: Boolean,
    default: false,
  },
});

const imageLoaded = ref(false);
const imageLoadError = ref(false);

watch(
  () => props.imageUrl,
  () => {
    imageLoaded.value = false;
    imageLoadError.value = false;
  },
);

const initials = computed(() => {
  if (!props.name) {
    return 'U';
  }
  return props.name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
});

function stringToHSLColour(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash % 360);
  return `hsl(${Math.abs(h)}, 40%, 80%)`;
}

function handleImageLoad() {
  imageLoaded.value = true;
}

function handleImageErr() {
  imageLoadError.value = true;
}
</script>

<style lang="scss" scoped>
.n-avatar__circle {
  border-radius: 100%;
  overflow: hidden;
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  img {
    width: 100%;
  }
  span {
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    line-height: 1;
    font-size: 1.2rem;
  }
  &--small {
    height: 30px;
    width: 30px;
    span {
      font-size: 0.8rem;
      line-height: 1;
    }
  }
  &--large {
    height: 75px;
    width: 75px;
    span {
      font-size: 2rem;
    }
  }
}
</style>
